import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from '@components/../lib/utils'

// shouldScaleBackground defaults to false: vaul otherwise forces document.body to
// solid black to fake the "card lift" effect, which blacks out the page behind
// side drawers and hides their content. Keep the page visible under the overlay.
const Drawer = ({ shouldScaleBackground = false, ...props }) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
)
Drawer.displayName = 'Drawer'

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay ref={ref} className={cn('fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm', className)} {...props} />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const directionClasses = {
  bottom: 'fixed inset-x-0 bottom-0',
  top: 'fixed inset-x-0 top-0',
  left: 'fixed inset-y-0 left-0',
  right: 'fixed inset-y-0 right-0'
}

const directionBaseClasses = (direction) => {
  if (direction === 'bottom') return 'mt-24 flex h-auto flex-col rounded-t-[10px]'
  if (direction === 'top') return 'flex h-auto flex-col rounded-b-[10px]'
  return 'h-full w-80 flex flex-col'
}

const DrawerContent = React.forwardRef(({ className, children, direction = 'right', ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        directionClasses[direction] ?? directionClasses.right,
        // Semantic tokens (not raw bg-white/dark:bg-slate-950) so the panel follows the
        // app's light theme instead of flipping to near-black under an OS dark color-scheme.
        'z-50 border border-border bg-card text-foreground',
        directionBaseClasses(direction),
        className
      )}
      {...props}
    >
      {direction === 'bottom' && <div className='mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted' />}
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
))
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({ className, ...props }) => <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({ className, ...props }) => <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title ref={ref} className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={cn('text-sm text-slate-500 dark:text-slate-400', className)} {...props} />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription
}
